import { FC, useCallback, useMemo, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';
import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useGiftCsMemoListQuery } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import {
  resetDeliveryToNotShared,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryShareChannel,
  GiftDeliveryShareStatus,
  GiftDeliveryStatus,
  GiftDeliverySummary,
  GiftDeliveryType,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  cancelGiftOrder,
  partialCancelGiftOrder,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderDeliveryMode,
  GiftOrderForAdmin,
  GiftOrderItemType,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { LowerRole } from '@shared/types';

import { copyAdminPreviewLink } from '../utils/adminPreviewLink';
import { prevStatusLabel, prevStatusMap } from '../utils/delivery.constants';
import { KakaoShareCallbackModal } from './couponDetail/KakaoShareCallbackModal';
import { CsMemoButton, splitCsMemos } from './csMemo';

const deliveryStatusLabel: Record<GiftDeliveryStatus, string> = {
  [GiftDeliveryStatus.WaitingAddress]: '주소대기',
  [GiftDeliveryStatus.AddressSubmitted]: '주소입력완료',
  [GiftDeliveryStatus.Preparing]: '준비중',
  [GiftDeliveryStatus.Shipping]: '배송중',
  [GiftDeliveryStatus.Delivered]: '배송완료',
  [GiftDeliveryStatus.Canceled]: '취소',
};

const deliveryStatusColor: Record<GiftDeliveryStatus, string> = {
  [GiftDeliveryStatus.WaitingAddress]: 'bg-orange-100 text-orange-800',
  [GiftDeliveryStatus.AddressSubmitted]: 'bg-cyan-100 text-cyan-800',
  [GiftDeliveryStatus.Preparing]: 'bg-yellow-100 text-yellow-800',
  [GiftDeliveryStatus.Shipping]: 'bg-blue-100 text-blue-800',
  [GiftDeliveryStatus.Delivered]: 'bg-green-100 text-green-800',
  [GiftDeliveryStatus.Canceled]: 'bg-gray-100 text-gray-500',
};

const shareStatusLabel: Record<GiftDeliveryShareStatus, string> = {
  [GiftDeliveryShareStatus.NotShared]: '미공유',
  [GiftDeliveryShareStatus.Shared]: '공유',
  [GiftDeliveryShareStatus.Viewed]: '열람',
};

const shareStatusColor: Record<GiftDeliveryShareStatus, string> = {
  [GiftDeliveryShareStatus.NotShared]: 'bg-gray-100 text-gray-500',
  [GiftDeliveryShareStatus.Shared]: 'bg-indigo-100 text-indigo-700',
  [GiftDeliveryShareStatus.Viewed]: 'bg-purple-100 text-purple-700',
};

const shareChannelLabel: Record<GiftDeliveryShareChannel, string> = {
  [GiftDeliveryShareChannel.KakaoTalk]: '카카오톡',
  [GiftDeliveryShareChannel.SMS]: '문자',
  [GiftDeliveryShareChannel.Link]: '링크',
  [GiftDeliveryShareChannel.Other]: '기타',
};

const fmt = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const fmtTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}:${min}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  if (n.length === 11) return `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}`;
  return phone;
};

const isDeliveryCancellable = (d: GiftDeliverySummary): boolean =>
  d.shareStatus === GiftDeliveryShareStatus.NotShared &&
  (d.deliveryStatus === GiftDeliveryStatus.WaitingAddress ||
    d.deliveryStatus === GiftDeliveryStatus.AddressSubmitted);

interface DeliveryDetailModalProps {
  deliveries: GiftDeliverySummary[];
  orderId?: string;
  order?: GiftOrderForAdmin;
  onClose: () => void;
}

export const DeliveryDetailModal: FC<DeliveryDetailModalProps> = ({
  deliveries,
  orderId,
  order,
  onClose,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [isResettingShare, setIsResettingShare] = useState(false);
  const [previewCopied, setPreviewCopied] = useState(false);
  const [kakaoCallbackDelivery, setKakaoCallbackDelivery] =
    useState<GiftDeliverySummary | null>(null);

  const sorted = useMemo(
    () => [...deliveries].sort((a, b) => a.seq - b.seq),
    [deliveries],
  );

  // CS 응대 메모 — 주문 축 1회 조회로 배송 단위 메모까지 함께 받는다
  const csMemoQuery = useGiftCsMemoListQuery({ giftOrder: orderId ?? '' }, !!orderId);
  const csMemoSplit = useMemo(() => splitCsMemos(csMemoQuery.data), [csMemoQuery.data]);
  const csMemoUnavailable = csMemoQuery.data === null;
  const csMemoSubtitle = order
    ? `주문자 ${order.receiverName ?? '-'} · ${order.receiverPhoneNumber ?? '-'}`
    : undefined;
  const csMemoSubLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of sorted) map.set(d._id, `배송 ${d.code}`);
    for (const c of order?.coupons ?? []) map.set(c._id, `쿠폰 ${c.code}-${c.seq}`);
    return map;
  }, [sorted, order]);

  const stats = useMemo(() => {
    const total = deliveries.length;
    const notShared = deliveries.filter(
      (d) => d.shareStatus === GiftDeliveryShareStatus.NotShared,
    ).length;
    const waitingAddress = deliveries.filter(
      (d) => d.deliveryStatus === GiftDeliveryStatus.WaitingAddress,
    ).length;
    const preparing = deliveries.filter(
      (d) => d.deliveryStatus === GiftDeliveryStatus.Preparing,
    ).length;
    const shipping = deliveries.filter(
      (d) => d.deliveryStatus === GiftDeliveryStatus.Shipping,
    ).length;
    const delivered = deliveries.filter(
      (d) => d.deliveryStatus === GiftDeliveryStatus.Delivered,
    ).length;
    return { total, notShared, waitingAddress, preparing, shipping, delivered };
  }, [deliveries]);

  const cancellableDeliveries = useMemo(
    () => sorted.filter(isDeliveryCancellable),
    [sorted],
  );

  const canCancel = useMemo(() => {
    if (!orderId || !order) return false;
    const status = order.paymentDetail.status;
    return (
      status === GiftOrderPaymentStatus.Paid ||
      status === GiftOrderPaymentStatus.Confirmed
    );
  }, [orderId, order]);

  const hasCouponItems = useMemo(() => {
    if (!order) return false;
    return order.orderDetails.some((d) => d.type === GiftOrderItemType.Coupon);
  }, [order]);

  const allNotSharedCoupons = useMemo(() => {
    if (!order || !order.coupons) return true;
    const coupons = order.coupons as { shareStatus: string; redeemStatus: string }[];
    return coupons.every(
      (c) =>
        c.shareStatus === GiftCouponShareStatus.NotShared &&
        c.redeemStatus === GiftCouponRedeemStatus.Available,
    );
  }, [order]);

  const isFullCancel = useMemo(() => {
    if (!canCancel) return false;
    const activeDeliveries = deliveries.filter(
      (d) => d.deliveryStatus !== GiftDeliveryStatus.Canceled,
    );
    const allDeliveriesSelected =
      activeDeliveries.length > 0 &&
      activeDeliveries.every((d) => selectedIds.has(d._id));
    if (!allDeliveriesSelected) return false;
    if (hasCouponItems) return allNotSharedCoupons;
    return true;
  }, [canCancel, deliveries, selectedIds, hasCouponItems, allNotSharedCoupons]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === cancellableDeliveries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(cancellableDeliveries.map((d) => d._id)));
    }
  }, [cancellableDeliveries, selectedIds.size]);

  const handleRevertStatus = useCallback(
    async (d: GiftDeliverySummary) => {
      const prev = prevStatusMap[d.deliveryStatus];
      if (!prev || revertingId) return;
      const label = prevStatusLabel[d.deliveryStatus] || '이전 상태로 되돌리기';
      if (!window.confirm(`[${d.code}] ${label} 하시겠습니까?`)) return;

      setRevertingId(d._id);
      setCancelResult(null);
      try {
        await updateGiftDeliveryStatus(d._id, prev);
        setCancelResult('배송상태가 되돌려졌습니다.');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '상태 되돌리기에 실패했습니다.';
        setCancelResult(`오류: ${message}`);
        setRevertingId(null);
      }
    },
    [revertingId],
  );

  // 공유상태 뱃지 클릭 → 미공유(NotShared)로 되돌리기.
  // 일괄배송 등 공유 개념이 없는데 잘못 Shared/Viewed 처리된 건을 운영자가 정정해
  // 취소 가능 상태로 전환하는 용도. (쿠폰 상세의 handleResetShareStatus 와 대응)
  const handleResetShareStatus = useCallback(
    async (d: GiftDeliverySummary) => {
      if (isResettingShare) return;
      if (
        !window.confirm(
          `[${d.code}] 공유상태를 미공유로 되돌리시겠습니까?\n(취소 가능 상태로 전환됩니다)`,
        )
      )
        return;

      setIsResettingShare(true);
      setCancelResult(null);
      try {
        await resetDeliveryToNotShared(d._id);
        setCancelResult('공유상태가 미공유로 변경되었습니다.');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : '공유상태 변경에 실패했습니다.';
        setCancelResult(`오류: ${message}`);
        setIsResettingShare(false);
      }
    },
    [isResettingShare],
  );

  const handleCancelRequest = useCallback(async () => {
    if (!orderId || selectedIds.size === 0 || !cancelReason.trim()) return;

    setIsCancelling(true);
    setCancelResult(null);

    try {
      if (isFullCancel) {
        await cancelGiftOrder(
          { _id: orderId, cancelReason: cancelReason.trim() },
          LowerRole.Admin,
        );
        setCancelResult('전체취소 요청이 완료되었습니다.');
      } else {
        const result = await partialCancelGiftOrder({
          _id: orderId,
          cancelReason: cancelReason.trim(),
          couponIds: [],
          deliveryIds: Array.from(selectedIds),
        });
        setCancelResult(
          `부분취소 완료: ${result.data.cancelledPrice.toLocaleString()}원 환불`,
        );
      }
      setShowCancelConfirm(false);
      setSelectedIds(new Set());
      setCancelReason('');
      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 1500);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : '취소 처리 중 오류가 발생했습니다.';
      setCancelResult(`오류: ${message}`);
    } finally {
      setIsCancelling(false);
    }
  }, [orderId, selectedIds, cancelReason, isFullCancel, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-gray-900">배송 상세내역</h2>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>
                전체 <span className="font-semibold text-gray-700">{stats.total}</span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                미공유{' '}
                <span
                  className={`font-semibold ${
                    stats.notShared > 0 ? 'text-orange-600' : 'text-gray-400'
                  }`}
                >
                  {stats.notShared}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                주소대기{' '}
                <span
                  className={`font-semibold ${
                    stats.waitingAddress > 0 ? 'text-orange-600' : 'text-gray-400'
                  }`}
                >
                  {stats.waitingAddress}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                준비중{' '}
                <span
                  className={`font-semibold ${
                    stats.preparing > 0 ? 'text-yellow-600' : 'text-gray-400'
                  }`}
                >
                  {stats.preparing}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                배송중{' '}
                <span
                  className={`font-semibold ${
                    stats.shipping > 0 ? 'text-blue-600' : 'text-gray-400'
                  }`}
                >
                  {stats.shipping}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span>
                완료{' '}
                <span
                  className={`font-semibold ${
                    stats.delivered > 0 ? 'text-green-600' : 'text-gray-400'
                  }`}
                >
                  {stats.delivered}
                </span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {orderId && (
              <CsMemoButton
                variant="button"
                label="CS메모"
                target={{ giftOrder: orderId }}
                title="주문 CS 응대 메모"
                subtitle={csMemoSubtitle}
                memos={csMemoSplit.all}
                isLoading={csMemoQuery.isLoading}
                unavailable={csMemoUnavailable}
                subLabelMap={csMemoSubLabelMap}
                notice="주문·쿠폰·배송 메모 통합 · 입력은 주문 단위로 저장"
              />
            )}
            {orderId && order && (
              <button
                onClick={() => {
                  const name = encodeURIComponent(order.receiverName ?? '');
                  const phone = encodeURIComponent(order.receiverPhoneNumber ?? '');
                  const key = encodeURIComponent(
                    process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '',
                  );
                  // 일괄배송은 delivery-status, 개별배송은 delivery 경로 사용
                  const path =
                    order.deliveryMode === GiftOrderDeliveryMode.Bulk
                      ? 'delivery-status'
                      : 'delivery';
                  const bugoId = order.bugo?._id;
                  const bugoQuery = bugoId ? `&bugoId=${encodeURIComponent(bugoId)}` : '';
                  const url = `${window.location.origin}/gift-store/order/${orderId}/${path}?name=${name}&phoneNumber=${phone}&secretKey=${key}${bugoQuery}`;
                  navigator.clipboard.writeText(url).then(() => {
                    setPreviewCopied(true);
                    setTimeout(() => setPreviewCopied(false), 2000);
                  });
                }}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                  previewCopied
                    ? 'bg-green-50 text-green-600'
                    : 'text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                {previewCopied ? '복사됨!' : '미리보기 URL 복사'}
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
        {/* Cancel result banner */}
        {cancelResult && (
          <div
            className={`mx-4 mb-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
              cancelResult.startsWith('오류')
                ? 'bg-red-50 text-red-700'
                : 'bg-green-50 text-green-700'
            }`}
          >
            {cancelResult}
          </div>
        )}

        <div className="px-4 pb-4">
          {/* Cancel action bar */}
          {canCancel && cancellableDeliveries.length > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2.5">
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size === cancellableDeliveries.length &&
                      cancellableDeliveries.length > 0
                    }
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  취소 가능 전체선택 ({cancellableDeliveries.length}건)
                </label>
                {selectedIds.size > 0 && (
                  <span className="text-xs text-red-600">
                    {selectedIds.size}건 선택됨
                    {isFullCancel ? ' (전체취소)' : ' (부분취소)'}
                  </span>
                )}
              </div>
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="rounded-lg bg-red-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-red-700"
                >
                  선택 배송 취소 ({selectedIds.size}건)
                </button>
              )}
            </div>
          )}

          {/* Table */}
          <div
            className="overflow-auto rounded-md border"
            style={{ maxHeight: 'calc(90vh - 140px)' }}
          >
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-gray-500">
                  {canCancel && cancellableDeliveries.length > 0 && (
                    <th className="whitespace-nowrap px-3 py-2 font-medium">선택</th>
                  )}
                  <th className="whitespace-nowrap px-3 py-2 font-medium">#</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">코드</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">유형</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">수량</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">배송상태</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">열람일</th>
                  <th
                    className="whitespace-nowrap px-3 py-2 font-medium"
                    title="카카오톡 공유 웹훅으로 집계된 채팅방 수 (클릭 시 상세 이력)"
                  >
                    카톡공유기록
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유채널</th>
                  <th
                    className="whitespace-nowrap px-3 py-2 font-medium"
                    title="카카오톡 공유 웹훅 기준 실제 전달 시각"
                  >
                    카톡전송일시
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">수령인</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">주소입력일</th>
                  {orderId && (
                    <th
                      className="whitespace-nowrap px-3 py-2 font-medium"
                      title="CS 응대 메모 (클릭 시 메모장)"
                    >
                      CS메모
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((d) => {
                  const cancellable = isDeliveryCancellable(d);
                  const isSelected = selectedIds.has(d._id);
                  const isCanceled = d.deliveryStatus === GiftDeliveryStatus.Canceled;
                  const revertTo = prevStatusMap[d.deliveryStatus];
                  const canRevert = !isCanceled && !!revertTo;

                  return (
                    <tr
                      key={d._id}
                      className={`${
                        isCanceled
                          ? 'bg-gray-50 opacity-60'
                          : isSelected
                          ? 'bg-red-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {canCancel && cancellableDeliveries.length > 0 && (
                        <td className="whitespace-nowrap px-3 py-1.5">
                          {cancellable ? (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(d._id)}
                              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">
                        {d.seq ?? ''}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 font-mono font-medium text-gray-800">
                        <button
                          type="button"
                          onClick={() => copyAdminPreviewLink('delivery', d.code)}
                          title="클릭 시 관리자 확인용(비기록) 링크 복사"
                          className="underline decoration-dotted underline-offset-2 hover:text-indigo-600 active:opacity-70"
                        >
                          {d.code}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                            d.type === GiftDeliveryType.Individual
                              ? 'bg-teal-100 text-teal-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {d.type === GiftDeliveryType.Individual ? '개별' : '일괄'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                        {d.quantity}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {canRevert ? (
                          <button
                            type="button"
                            disabled={revertingId === d._id}
                            onClick={() => handleRevertStatus(d)}
                            title={`클릭 시 ${
                              prevStatusLabel[d.deliveryStatus] ?? '이전 상태로 되돌리기'
                            }`}
                            className="inline-flex items-center gap-0.5 rounded-full ring-1 ring-transparent transition hover:ring-red-300 active:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <span
                              className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                                deliveryStatusColor[d.deliveryStatus]
                              }`}
                            >
                              {deliveryStatusLabel[d.deliveryStatus]}
                            </span>
                            <svg
                              className="h-3 w-3 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 14l-4-4m0 0l4-4m-4 4h11a4 4 0 010 8h-1"
                              />
                            </svg>
                          </button>
                        ) : (
                          <span
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                              deliveryStatusColor[d.deliveryStatus]
                            }`}
                          >
                            {deliveryStatusLabel[d.deliveryStatus]}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {!isCanceled &&
                        d.shareStatus !== GiftDeliveryShareStatus.NotShared ? (
                          <button
                            type="button"
                            disabled={isResettingShare}
                            onClick={() => handleResetShareStatus(d)}
                            title="클릭 시 미공유로 되돌리기 (취소 가능 상태로 전환)"
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight underline decoration-dotted underline-offset-2 active:opacity-70 disabled:opacity-50 ${
                              shareStatusColor[d.shareStatus]
                            }`}
                          >
                            {shareStatusLabel[d.shareStatus]}
                          </button>
                        ) : d.type === GiftDeliveryType.Individual ? (
                          <span
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
                              shareStatusColor[d.shareStatus]
                            }`}
                          >
                            {shareStatusLabel[d.shareStatus]}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {d.type === GiftDeliveryType.Individual
                          ? d.sharedAt
                            ? fmt(d.sharedAt)
                            : '-'
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {d.type === GiftDeliveryType.Individual
                          ? d.viewedAt
                            ? fmt(d.viewedAt)
                            : '-'
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {d.type === GiftDeliveryType.Individual &&
                        d.shareKakaoCount &&
                        d.shareKakaoCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => setKakaoCallbackDelivery(d)}
                            title={
                              d.shareKakaoCount >= 2
                                ? `⚠ 중복노출 — 이 배송이 ${d.shareKakaoCount}개 서로 다른 카톡방으로 전달됨 (클릭 시 상세 이력)`
                                : `카카오톡 ${d.shareKakaoCount}개 채팅방에 전달됨 — 클릭 시 상세 이력`
                            }
                            className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold active:opacity-80 ${
                              d.shareKakaoCount >= 2
                                ? 'bg-red-500 text-white ring-1 ring-red-600'
                                : 'bg-kakao-yellow text-kakao-brown'
                            }`}
                          >
                            {d.shareKakaoCount}
                          </button>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">
                        {d.type === GiftDeliveryType.Individual && d.lastSharedChannel
                          ? shareChannelLabel[d.lastSharedChannel]
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {d.type === GiftDeliveryType.Individual && d.kakaoSharedAt
                          ? fmtTime(d.kakaoSharedAt)
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
                        {d.recipient ? (
                          <span>
                            {d.recipient.name}{' '}
                            <span className="text-gray-400">
                              {fmtPhone(d.recipient.phoneNumber)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {d.recipient?.submittedAt
                          ? fmtTime(d.recipient.submittedAt)
                          : '-'}
                      </td>
                      {orderId && (
                        <td className="whitespace-nowrap px-3 py-1.5">
                          <CsMemoButton
                            target={{ giftDelivery: d._id }}
                            title={`배송 ${d.code} · CS 응대 메모`}
                            subtitle={csMemoSubtitle}
                            memos={csMemoSplit.byDelivery.get(d._id) ?? []}
                            isLoading={csMemoQuery.isLoading}
                            unavailable={csMemoUnavailable}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cancel confirmation dialog */}
        {showCancelConfirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/40"
              onClick={() => !isCancelling && setShowCancelConfirm(false)}
            />
            <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-bold text-gray-900">
                {isFullCancel ? '전체취소 확인' : '부분취소 확인'}
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {isFullCancel
                  ? `주문 전체를 취소합니다. 전액 환불됩니다.`
                  : `선택한 배송 ${selectedIds.size}건을 취소합니다. 해당 금액만 환불됩니다.`}
              </p>

              {isFullCancel && hasCouponItems && (
                <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  쿠폰 상품도 함께 전체취소됩니다.
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  취소 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="취소 사유를 입력하세요"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
                  rows={3}
                />
              </div>

              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={isCancelling}
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  닫기
                </button>
                <button
                  onClick={handleCancelRequest}
                  disabled={isCancelling || !cancelReason.trim()}
                  className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {isCancelling
                    ? '처리중...'
                    : isFullCancel
                    ? '전체취소 실행'
                    : '부분취소 실행'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {kakaoCallbackDelivery && (
        <KakaoShareCallbackModal
          coupon={kakaoCallbackDelivery}
          onClose={() => setKakaoCallbackDelivery(null)}
        />
      )}
    </div>
  );
};
